"use strict";

/* ------------------------------------------------------------------ *
 * Mappers 24 and 26: VRC6a / VRC6b
 *
 * Konami's VRC6 is one chip in two pin variants whose only difference is
 * that A0 and A1 are swapped on the register bus. VRC6a is mapper 24
 * (Akumajou Densetsu); VRC6b is mapper 26 (Madara, Esper Dream 2). Every
 * address below is a VRC6a one; on a VRC6b the low two address bits are
 * exchanged first, so the byte a game writes to $9001 arrives at $9002.
 *
 *   $8000-$8003   16 KB PRG bank at $8000-$BFFF       (low 4 bits)
 *   $9000-$9003   audio: pulse 1 and the frequency control
 *   $A000-$A002   audio: pulse 2
 *   $B000-$B002   audio: sawtooth
 *   $B003         PPU banking style: mirroring in bits 2-3, PRG-RAM enable
 *                 in bit 7, CHR banking mode in bits 0-1
 *   $C000-$C003   8 KB PRG bank at $C000-$DFFF
 *   $D000-$D003   CHR R0-R3, 1 KB each across $0000-$0FFF
 *   $E000-$E003   CHR R4-R7, 1 KB each across $1000-$1FFF
 *   $F000         IRQ latch
 *   $F001         IRQ control: bit 0 reload-after-ack, bit 1 enable,
 *                 bit 2 mode (0 = scanline, 1 = CPU cycle)
 *   $F002         IRQ acknowledge
 *
 * $E000-$FFFF is always the last 8 KB bank. Only address lines 0, 1 and
 * 12-15 reach the chip, so every register is decoded on `address & $F003`
 * and $DE6A is the same register as $D002.
 *
 * Two deliberate simplifications, neither of which any released game can
 * tell apart from the real chip:
 *
 * - $B003 bits 0-1 select one of four arrangements of the eight CHR
 *   registers. All three commercial VRC6 games write only
 *   $20/$24/$28/$2C/$A0/$A4/$A8/$AC, which is mode 0, and the wiki's table
 *   for the other three is built out of row spans that do not survive
 *   flattening, so the registers are always taken in order.
 * - $B003 bit 4 (name tables out of CHR ROM) and bit 5 (CHR A10
 *   passthrough) are not modelled. The games leave bit 5 set and never set
 *   bit 4, and this PPU has no CHR-ROM name tables to switch to.
 * ------------------------------------------------------------------ */

function register(Mappers) {
  function VRC6(nes) {
    this.nes = nes;
  }

  VRC6.prototype = Object.create(Mappers[0].prototype);
  VRC6.prototype.constructor = VRC6;

  // The IRQ counter runs on CPU cycles in cycle mode, and on 341-dot PPU
  // stretches in scanline mode. nes.js calls clockCpuCycles() after every CPU
  // burst, so the counter is fed the clock the chip actually sees rather than a
  // once-per-line approximation.
  VRC6.prototype.watchesCpuCycles = true;

  VRC6.prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    this.prgBank16 = 0; // $8000
    this.prgBank8 = 0; // $C000
    this.chrBank1k = [0, 0, 0, 0, 0, 0, 0, 0];
    this.bankingStyle = 0; // $B003
    this.prgRamEnabled = false;
    this.chrBankingMode = 0;

    // IRQ: an 8-bit counter that counts up from the latch to $100, then
    // reloads. The line is released by any write to $F000-$F002.
    this.irqLatch = 0;
    this.irqCounter = 0;
    this.irqMode = 0;
    this.irqEnabled = false;
    this.irqReloadOnAck = false;
    this.irqPending = false;
    this.irqOnCpu = false;
    this.cycleCounter = 0;
  };

  // A VRC6b reaches A1 where a VRC6a reaches A0, and the other way round.
  VRC6.prototype.decodeRegister = function (address) {
    var register = address & 0xf003;
    if (this.nes.rom.mapperType === 26) {
      var low = register & 0x03;
      register = (register & 0xfffc) | ((low & 1) << 1) | ((low & 2) >> 1);
    }
    return register;
  };

  VRC6.prototype.write = function (address, value) {
    if (address < 0x8000) {
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    var register = this.decodeRegister(address);
    var page = register & 0xf000;

    // The audio channels share the decoder with the bank registers, so they are
    // routed before the switch below. $B003 is the one address in the block that
    // is not audio: it is the PPU banking style.
    if (page === 0x9000 || page === 0xa000 || page === 0xb000) {
      if (register === 0xb003) {
        this.bankingStyle = value;
        this.prgRamEnabled = (value & 0x80) !== 0;
        this.chrBankingMode = value & 0x03;
        this.updateMirroring();
        return;
      }
      this.nes.papu.exWrite(register, value);
      return;
    }

    switch (register) {
      case 0x8000:
        this.prgBank16 = value & 0x0f;
        this.updatePrgBanks();
        break;

      case 0xc000:
        this.prgBank8 = value & 0x3f;
        this.updatePrgBanks();
        break;

      case 0xd000:
      case 0xd001:
      case 0xd002:
      case 0xd003:
        this.chrBank1k[register & 3] = value;
        this.updateChrBanks();
        break;

      case 0xe000:
      case 0xe001:
      case 0xe002:
      case 0xe003:
        this.chrBank1k[4 + (register & 3)] = value;
        this.updateChrBanks();
        break;

      case 0xf000:
        this.irqLatch = value;
        this.acknowledgeIrq();
        break;

      case 0xf001:
        this.irqMode = value & 0x04;
        this.irqEnabled = (value & 0x02) !== 0;
        this.irqReloadOnAck = (value & 0x01) !== 0;
        if (this.irqEnabled) {
          // Enabling reloads the counter from the latch and restarts the
          // fractional part of the divider.
          this.irqCounter = this.irqLatch;
          this.cycleCounter = 0;
        }
        this.acknowledgeIrq();
        break;

      case 0xf002:
        // Re-arm without touching the counter, and release the line.
        this.irqEnabled = this.irqReloadOnAck;
        this.acknowledgeIrq();
        break;

      default:
        // $8001-$8003, $9003-$AFF3 and every other mirror: not decoded.
        break;
    }
  };

  VRC6.prototype.updatePrgBanks = function () {
    var rom = this.nes.rom;
    var banks8k = rom.romCount * 2;
    if (banks8k === 0) {
      return;
    }

    // $8000-$BFFF is a 16 KB window, so the register holds a 16 KB bank number.
    this.loadRomBank(this.prgBank16, 0x8000);
    // $C000-$DFFF is one 8 KB window from the second register.
    this.load8kRomBank(this.prgBank8 % banks8k, 0xc000);
    // $E000-$FFFF is soldered to the last 8 KB of the image.
    this.load8kRomBank(banks8k - 1, 0xe000);
  };

  VRC6.prototype.updateChrBanks = function () {
    var banks1k = this.nes.rom.vromCount * 4;
    if (banks1k === 0) {
      // CHR-RAM: the pattern tables belong to the PPU and the registers are
      // inert, but they still have to be kept for a save state.
      return;
    }

    for (var i = 0; i < 8; i++) {
      this.load1kVromBank(this.chrBank1k[i] % banks1k, i * 0x400);
    }
  };

  // $B003 bits 2-3 pick one of the four arrangements mode 0 defines.
  VRC6.prototype.updateMirroring = function () {
    switch ((this.bankingStyle >> 2) & 0x03) {
      case 0:
        this.setMirroring(this.nes.rom.VERTICAL_MIRRORING);
        break;
      case 1:
        this.setMirroring(this.nes.rom.HORIZONTAL_MIRRORING);
        break;
      case 2:
        this.setMirroring(this.nes.rom.SINGLESCREEN_MIRRORING);
        break;
      default:
        this.setMirroring(this.nes.rom.SINGLESCREEN_MIRRORING2);
        break;
    }
  };

  // One burst of CPU cycles. In cycle mode the counter advances once per CPU
  // cycle. In scanline mode one step is one 341-dot PPU line, which is three CPU
  // cycles' worth of dots, so the burst is converted into dots first.
  VRC6.prototype.clockCpuCycles = function (cycles) {
    if (!this.irqEnabled) {
      return;
    }

    if (this.irqMode !== 0) {
      this.cycleCounter += cycles;
      while (this.cycleCounter > 0) {
        this.cycleCounter--;
        this.irqCounter++;
        if ((this.irqCounter & 0x100) !== 0) {
          this.irqCounter = this.irqLatch;
          this.raiseIrq();
        }
      }
      return;
    }

    this.cycleCounter += cycles * 3;
    while (this.cycleCounter >= 341) {
      this.cycleCounter -= 341;
      this.irqCounter++;
      if (this.irqCounter === 0x100) {
        this.irqCounter = this.irqLatch;
        this.raiseIrq();
      }
    }
  };

  VRC6.prototype.raiseIrq = function () {
    this.irqPending = true;
    this.refreshCpuIrq();
  };

  // $F000, $F001 and $F002 all release the line; the counter keeps running.
  VRC6.prototype.acknowledgeIrq = function () {
    this.irqPending = false;
    this.refreshCpuIrq();
  };

  // /IRQ is the product of the enable and pending flags. This CPU latches an IRQ
  // request rather than watching a pin, so the request is dropped again when the
  // pin goes back high.
  VRC6.prototype.refreshCpuIrq = function () {
    var cpu = this.nes.cpu;

    if (this.irqPending) {
      if (!this.irqOnCpu) {
        cpu.requestIrq(cpu.IRQ_NORMAL);
        this.irqOnCpu = true;
      }
      return;
    }

    if (this.irqOnCpu && cpu.irqRequested && cpu.irqType === cpu.IRQ_NORMAL) {
      cpu.irqRequested = false;
    }
    this.irqOnCpu = false;
  };

  VRC6.prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("VRC6: Invalid ROM! Unable to load.");
    }

    this.reset();

    // The board carries CHR ROM: Akumajou Densetsu has 128 KB of it. This used
    // to be skipped, so only whatever load1kVromBank() happened to fetch was in
    // the pattern tables.
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();

    this.updatePrgBanks();
    this.updateChrBanks();
    this.updateMirroring();

    this.resetIrqHandler();
  };

  VRC6.prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.prgBank16 = this.prgBank16;
    s.prgBank8 = this.prgBank8;
    s.chrBank1k = this.chrBank1k.slice();
    s.bankingStyle = this.bankingStyle;
    s.prgRamEnabled = this.prgRamEnabled;
    s.chrBankingMode = this.chrBankingMode;
    s.irqLatch = this.irqLatch;
    s.irqCounter = this.irqCounter;
    s.irqMode = this.irqMode;
    s.irqEnabled = this.irqEnabled;
    s.irqReloadOnAck = this.irqReloadOnAck;
    s.irqPending = this.irqPending;
    s.irqOnCpu = this.irqOnCpu;
    s.cycleCounter = this.cycleCounter;
    return s;
  };

  VRC6.prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.prgBank16 = s.prgBank16;
    this.prgBank8 = s.prgBank8;
    this.chrBank1k = s.chrBank1k.slice();
    this.bankingStyle = s.bankingStyle;
    this.prgRamEnabled = s.prgRamEnabled;
    this.chrBankingMode = s.chrBankingMode;
    this.irqLatch = s.irqLatch;
    this.irqCounter = s.irqCounter;
    this.irqMode = s.irqMode;
    this.irqEnabled = s.irqEnabled;
    this.irqReloadOnAck = s.irqReloadOnAck;
    this.irqPending = s.irqPending;
    this.cycleCounter = s.cycleCounter;
    // The CPU and PPU keep their own copies of the banks, so re-derive them.
    this.irqOnCpu = false;
    this.updatePrgBanks();
    this.updateChrBanks();
    this.updateMirroring();
    this.refreshCpuIrq();
  };

  Mappers[24] = VRC6;
  Mappers[26] = VRC6;
}

module.exports = { register: register };
